import { useState } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useTranslations } from 'next-intl';
import { useMediaQuery } from 'usehooks-ts';

import { triangleDown as IconTriangleDown } from '@/components/icons/triangleDown';
import { commonIntervals } from '@/components/interval-button/utils';
import TooltipPortal from '@/components/TooltipPortal';
import { FEATURES } from '@/constants';
import type { Interval } from '@/hooks/api/api.types';
import { useBitcoinPrice } from '@/hooks/api/useBitcoinPrice';
import { useHistoryPrice } from '@/hooks/api/useHistoryPrice';
import { cn } from '@/utils/cn';

import ChainlinkBanner from '../../sidebar/ChainlinkBanner';

import BannerContent from './BannerContent';

interface BitcoinBannerProps {
  isOnlyChanging?: boolean;
}

const TOOLTIP_CONTENT_CLASSES =
  'data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade select-none rounded-md px-4 py-2 will-change-[transform,opacity] tooltip-surface z-50';

const BitcoinBanner: React.FC<BitcoinBannerProps> = ({ isOnlyChanging }: BitcoinBannerProps) => {
  const [interval, setInterval] = useState<Interval>('24h');
  const t = useTranslations('home.status');
  // Match BannerContent's CSS breakpoint without starting a hidden read during hydration.
  const isWideScreen = useMediaQuery('(min-width: 1320px)', { initializeWithValue: false });

  const { data: price, priceStatus } = useBitcoinPrice();
  const isLoadingPrice = !price && priceStatus === 'loading';
  const { data: bitcoinData, isLoading, isError } = useHistoryPrice({ interval, enabled: !!isOnlyChanging || isWideScreen });

  const parsedChange = bitcoinData ? Number(bitcoinData.change) : undefined;
  const change = !isError && parsedChange !== undefined && Number.isFinite(parsedChange) ? parsedChange : undefined;
  const isDown = change !== undefined && change < 0;

  const onIntervalClick = () => {
    setInterval((v) => {
      const index = commonIntervals.indexOf(v);
      const next = index === 3 ? 0 : index + 1;

      return commonIntervals[next];
    });
  };

  if (isOnlyChanging) {
    // TODO: reuse this code
    return (
      <button
        type="button"
        className={cn('flex flex-row select-none gap-1 items-center group', {
          'text-text-secondary': isLoading,
          'text-red-100': isDown && !isLoading,
          'text-green-100': !isDown && !isLoading,
        })}
        onClick={onIntervalClick}
      >
        <div className={cn({ 'rotate-180': !isDown, 'animate-pulse': isLoading })}>{change !== undefined && <IconTriangleDown />}</div>
        <p className={cn('text-sm', { 'animate-pulse': isLoading })}>
          {change === undefined ? t('notAvailable') : `${change.toString().replace('-', '')}%`}
        </p>
        <p className="uppercase group-hover:opacity-75 text-sm">({interval})</p>
      </button>
    );
  }

  if (!FEATURES.chainlinkBanner) {
    return (
      <BannerContent
        price={price?.USD}
        isLoadingPrice={isLoadingPrice}
        change={change}
        isLoading={isLoading}
        isDown={isDown}
        interval={interval}
        onIntervalClick={onIntervalClick}
      />
    );
  }

  return (
    <Tooltip.Provider delayDuration={0}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <div>
            <BannerContent
              price={price?.USD}
              isLoadingPrice={isLoadingPrice}
              change={change}
              isLoading={isLoading}
              isDown={isDown}
              interval={interval}
              onIntervalClick={onIntervalClick}
            />
          </div>
        </Tooltip.Trigger>
        <TooltipPortal>
          <Tooltip.Content side="bottom" sideOffset={2} className={TOOLTIP_CONTENT_CLASSES}>
            <ChainlinkBanner isTooltip={true} />
            <Tooltip.Arrow className="fill-tooltip-surface w-4 h-2" />
          </Tooltip.Content>
        </TooltipPortal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export default BitcoinBanner;
